import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { useElements, useStripe, CardElement } from '@stripe/react-stripe-js';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { Country, State, City } from 'country-state-city';
import * as alertActions from '../../stores/actions/alertActions';
import { Autocomplete } from '@material-ui/lab';
import { TextField } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { attachPaymentMethod } from '../../api/general/users';

const styles = (theme) => ({
	root: {
		padding: '20px',
		borderRadius: '5px',
		width: '100%',
		[theme.breakpoints.down('sm')]:
			{
				padding: 0
			}
	},
	title: {
		width: '100%',
		textAlign: 'center',
		fontSize: '24px',
		fontWeight: 500,
		color: 'white',
		marginBottom: '30px',
		[theme.breakpoints.down('sm')]:
			{
				marginBottom: '10px'
			},
		'&.modal': {
			color: 'black'
		}
	},
	row: {
		width: '100%',
		marginBottom: '15px'
	},
	rowPaymentInput: {
		border: '1px solid var(--blue_300)',
		height: '63px',
		paddingTop: '22px',
		paddingLeft: '10px',
		borderRadius: '30px',
		marginBottom: '15px',
		fontSize: '14px',
		backgroundColor: 'white',
		[theme.breakpoints.down('sm')]:
			{
				paddingTop: '12px',
				height: '42px',
				marginBottom: '10px'
			}
	},
	inputForm: {
		width: '100%',
		height: '63px',
		borderRadius: '30px',
		border: '1px solid var(--blue_300)',
		fontSize: '16px',
		fontWeight: 600,
		paddingLeft: '24px',
		[theme.breakpoints.down('sm')]:
			{
				fontSize: '14px',
				fontWeight: 500,
				height: '42px'
			},

		'&:focus': {
			outline: 'none'
		}
	},
	columnSelect: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		marginBottom: '15px',

		'& .MuiAutocomplete-root': {
			width: '100%',
			position: 'relative',
			border: '1px solid var(--blue_300)',
			padding: '0 24px',
			height: '63px',
			borderRadius: '30px',
			backgroundColor: 'white',
			[theme.breakpoints.down('sm')]:
			{
				height: '42px'
			},

			'& .MuiAutocomplete-inputRoot': {
				height: '100%',
				marginTop: 0,
				borderBottom: 'none !important',
				outline: 'none'
			},

			'& .MuiFormControl-root': {
				height: '100%'
			},

			'& .MuiInputLabel-formControl': {
    		display: 'none'
			},

			'& .MuiInput-underline:before': {
				borderBottom: 'unset !important'
			},

			'&:focus': {
				outline: 'none',
				borderBottom: 'unset !important'
			}
		}
	},
	addressForm: {
		width: '100%',
		'&:first-child': {
			marginRight: '10px'
		}
	},
	btnContainer: {
		width: '100%',
		display: 'flex',
		justifyContent: 'center',
		marginTop: '30px',
		[theme.breakpoints.down('sm')]:
			{
				marginTop: '20px'
			},
		'&.modal': {
			'& button': {
				cursor: 'pointer',
				backgroundColor: 'var(--blue_300)',
				width: '100%',
				border: 'none',
				height: '40px',
				color: 'white',
				borderRadius: '5px',
				fontSize: '20px',
				fontWeight: 700
			}
		},

		'& button': {
			cursor: 'pointer',
			backgroundColor: '#096A86',
			width: '100%',
			border: 'none',
			height: '40px',
			color: 'white',
			fontSize: '20px',
			fontWeight: 700,
			borderRadius: '25px',

			'&:hover': {
				backgroundColor: 'var(--blue_300)'
			}
		}
	}
});

const PaymentMethodForm = (props) =>
{
	const { classes, onSubmit, setAlert, modal } = props;
	const stripe = useStripe();
	const elements = useElements();
	const intl = useIntl();
	const [cardInfo, setCardInfo] = useState({
		name: '',
		address: '',
		email: ''
	});
	const [ countries, setCountires ] = useState([]);
	const [ states, setStates ] = useState([]);
	const [ cities, setCities ] = useState([]);
	const [cardComplete, setCardComplete] = useState(false);
	const [ selectedLocation, setSelectedLocation ] = useState({ country: {}, city: {}, state: {} });
	const errorTitle = intl.formatMessage({ id: 'payment.label.failure', defaultMessage: 'Payment integration was failed' });
	const dismissAction =intl.formatMessage({ id: 'alert.dismiss', defaultMessage: 'Dismiss' });

	function handleChangeAddressLine(e)
	{
		const { value } = e.target;

		setCardInfo((prev) =>
		{
			return { ...prev, address: { ...prev.address, line: value } };
		});
	}

	function handleChangeName(e)
	{
		const { value } = e.target;

		setCardInfo((prev) =>
		{
			return { ...prev, name: value };
		});
	}

	function handleChangeEmail(e)
	{
		const { value } = e.target;

		setCardInfo((prev) =>
		{
			return { ...prev, email: value };
		});
	}

	function parseForSelect(arr)
	{
		return arr.map((item) => ({
			label: item.name,
			value: item.isoCode ? item.isoCode : item.name
		}));
	}

	function handleSelectCountry(country)
	{
		const states = State.getStatesOfCountry(country.value);

		setSelectedLocation((prev) =>
		{
			return { ...prev, country };
		});

		setStates(parseForSelect(states));
	}

	function handleSelectState(state)
	{
		const cities = City.getCitiesOfState(selectedLocation.country.value, state.value);

		setSelectedLocation((prev) =>
		{
			return { ...prev, state };
		});

		setCities(parseForSelect(cities));
	}

	function handleSelectCity(city)
	{
		setSelectedLocation((prev) =>
		{
			return { ...prev, city };
		});
	}

	async function handleSubmit(event)
	{
		event.preventDefault();

		if (!cardComplete) return;

		try
		{
			const { error, paymentMethod } = await stripe.createPaymentMethod({
				type: 'card',
				card: elements.getElement(CardElement),
				billing_details: {
					name: cardInfo.name,
					email: cardInfo.email,
					address: {
						city: selectedLocation.city.value,
						state: selectedLocation.state.value,
						country: selectedLocation.country.value,
						line1: cardInfo.address.line
					}
				}
			});

			if (error)
			{
				console.error('Error creating payment method:', error);
				setAlert({ title: intl.formatMessage({ id: 'label.error', defaultMessage: 'Error' }), description: errorTitle, okTitle: dismissAction, type: 'error' });
			}
			else
			{
				// Proceed with handling the payment method or further actions
				await attachPaymentMethod({ paymentMethod });
				onSubmit(paymentMethod.id);
			}
		}
		catch (err)
		{
			/* Handle Error*/
			console.error('Error creating payment method:', err);
			setAlert({ title: intl.formatMessage({ id: 'label.error', defaultMessage: 'Error' }), description: errorTitle, okTitle: dismissAction, type: 'error' });
		}
	}

	const handleCardChange = (event) =>
	{
		// Check if the card details have been entered
		if (event.complete)
		{
			setCardComplete(true);
		}
		else
		{
			setCardComplete(false);
		}
	};

	useEffect(() =>
	{
		const allCountry = Country.getAllCountries();

		setCountires(parseForSelect(allCountry));
	}, []);

	return (
		<div className={classes.root}>
			<div className={classnames(classes.title, { modal })}>
				<FormattedMessage id="payment.label.add" defaultMessage="Add Payment Method"/>
			</div>
			<form onSubmit={handleSubmit}>
				<div className={classes.row}>
					<input
						className={classes.inputForm}
						required
						onChange={handleChangeName}
						type="text"
						name="name"
						placeholder={intl.formatMessage({ id: 'payment.label.holderName', defaultMessage: 'Cardholder Name' })}
					/>
				</div>
				<div className={classes.row}>
					<input
						className={classes.inputForm}
						required
						onChange={handleChangeEmail}
						type="email"
						name="email"
						placeholder={intl.formatMessage({ id: 'label.email', defaultMessage: 'Email' })}
					/>
				</div>
				<div className={classes.rowPaymentInput}>
					<CardElement onChange={handleCardChange} />
				</div>

				<div className={classes.addressWrapper}>
					<div className={classes.row}>
						<input
							className={classes.inputForm}
							onChange={handleChangeAddressLine}
							type="text"
							name="address"
							placeholder={intl.formatMessage({ id: 'payment.label.address', defaultMessage: 'Address' })}
							required
						/>
					</div>
					<div className={classes.columnSelect}>
						<div className={classes.addressForm}>
							<Autocomplete
								disableClearable
								name="country"
								options={countries}
								onChange={(event, newValue) => handleSelectCountry(newValue)}
								getOptionLabel={(option) => `${option.label}`}
								renderOption={(option) => (`${option.label}`)}
								renderInput={(params) => <TextField {...params} label={<FormattedMessage id="payment.label.country" defaultMessage="Country"/>} required />}
							/>
						</div>
					</div>
					<div className={classes.columnSelect}>
						<div className={classes.addressForm}>
							<Autocomplete
								disablePortal
								name="state"
								options={states}
								onChange={(event, newValue) => handleSelectState(newValue)}
								getOptionLabel={(option) => `${option.label}`}
								renderOption={(option) => (`${option.label}`)}
								renderInput={(params) => <TextField {...params} label={<FormattedMessage id="payment.label.state" defaultMessage="State"/>} required />}
							/>
						</div>
					</div>
					<div className={classes.columnSelect}>
						<div className={classes.addressForm}>
							<Autocomplete
								name="city"
								options={cities}
								onChange={(event, newValue) => handleSelectCity(newValue)}
								getOptionLabel={(option) => `${option.label}`}
								renderOption={(option) => (`${option.label}`)}
								renderInput={(params) => <TextField {...params} label={<FormattedMessage id="payment.label.city" defaultMessage="City"/>} required />}
							/>
						</div>
					</div>
					<div className={classnames(classes.btnContainer, { modal })}>
						<button type="submit"><FormattedMessage id="label.submit" defaultMessage="Submit"/></button>
					</div>
				</div>
			</form>
		</div>
	);
};

PaymentMethodForm.propTypes = {
	classes: PropTypes.object.isRequired,
	setAlert: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
	modal: PropTypes.bool
};

const mapStateToProps = (state) =>
{
	return {
		auth: state.auth
	};
};

const mapDispatchToProps = (dispatch) =>
	({
		setAlert: ({ title, description, okTitle, type }) =>
		{
			dispatch(alertActions.setAlert({ title, description, okTitle, type }));
		}
	});

export default connect(
	mapStateToProps,
	mapDispatchToProps,
	null
)(withStyles(styles)(PaymentMethodForm));
